import { createFileRoute } from "@tanstack/react-router";
import { canonicalLink, canonicalUrl, routeMeta } from "@/lib/seo";
import { pergolaHeadScripts, PERGOLA_HUB_CRUMB } from "@/lib/pergolaJsonLd";
import { ServicePage } from "@/components/ServicePage";
import { AustinSiblingCallouts } from "./patio-covers.in.austin";
import hero from "@/assets/original/stock-commercial.jpg";
import heroPicture from "@/assets/original/stock-commercial.jpg?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";

const PATH = "/louvered-patio-covers/in/austin";
const TITLE = "Louvered Patio Covers in Austin, TX | Texas Made Shade";
const DESC = "Motorized louvered roof patio covers in Austin, TX — louvers open for breeze, close for rain, with sensors and app control. Financing available.";

/** Q&A rendered on the page — also merged into the single FAQPage node in head(). */
const PAGE_FAQS = [
  { q: "Does it really keep rain off?", a: "Yes — closed louvers lap like shingles and perimeter beams contain integrated gutters that drain through the posts." },
  { q: "Which system do you install?", a: "Engineered aluminum louvered pergola systems with documented wind loads and a transferable warranty — installed by our own Seguin, TX crew." },
  { q: "Cost?", a: "Most Austin motorized louvered roofs run $95–$145/sq ft installed depending on span, motor count and sensors." },
  { q: "Can it auto-close in a storm?", a: "Yes — optional rain and wind sensors auto-close the louvers before weather hits." },
];

export const Route = createFileRoute("/louvered-patio-covers/in/austin")({
  head: (ctx) => ({
    links: [canonicalLink(PATH)],
    meta: routeMeta(ctx, {
      title: TITLE,
      description: DESC,
      image: hero,
      imageAlt: "Motorized louvered roof patio cover installed in Austin",
      socialTitle: "Louvered Patio Covers in Austin, TX",
      socialDescription: DESC,
      keywords: "louvered patio cover Austin, motorized louvered roof Austin TX, adjustable louver patio cover, aluminum louvered pergola Austin, opening roof patio cover, rain sensor louvered roof, motorized pergola Austin, louvered roof cost Austin, patio cover contractor Austin TX, outdoor living Austin TX, louvered pergola dealer Texas, pergola financing Austin, Acorn Finance pergola, Travis County patio covers, Round Rock louvered pergola, Cedar Park patio cover, retractable awning vs louvered roof, Seguin TX outdoor living",
    }),
    scripts: [
      ...pergolaHeadScripts({
        path: PATH,
        serviceName: "Louvered Patio Covers in Austin, TX",
        breadcrumbName: "Louvered Patio Covers in Austin",
        breadcrumbAncestors: [PERGOLA_HUB_CRUMB],
        visibleFaqs: PAGE_FAQS,
        description: DESC,
        faqs: [
          { q: "How does a louvered roof work?", a: "Aluminum louver blades rotate on a motorized shaft. Open them flat for full sun, tilt for filtered shade, or close them tight for full rain protection — controlled from a wall switch, remote, or smartphone app. Many systems include a rain sensor that auto-closes." },
          { q: "Will it actually keep rain off?", a: "Yes — when closed the louvers lap like roof shingles and the frame includes integrated gutters in the perimeter beams that drain through the posts. Important during Austin spring storms." },
          { q: "Which louvered roof system do you install?", a: "We install engineered aluminum louvered pergola systems — sealed drawings where required, documented wind loads, published foundation details and a transferable warranty. Every system is installed by our own crew out of Seguin, TX." },
          { q: "How much do louvered covers cost in Austin?", a: "Motorized louvered roofs in Austin typically run $95–$145 per square foot installed depending on span, motor count, sensors and finish. Pricier than a solid cover but far more flexible." },
        ],
      }),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Louvered Patio Covers in Austin, TX",
          serviceType: "Louvered Roof Installation",
          url: canonicalUrl(PATH),
          provider: { "@id": "https://texasmadeshade.com/#business" },
          areaServed: { "@type": "City", name: "Austin", containedInPlace: { "@type": "AdministrativeArea", name: "Travis County" } },
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <ServicePage
      currentPath={PATH}
      serviceName="Louvered Patio Covers in Austin"
      breadcrumbs={[
        { label: "Patio Covers", to: "/patio-covers/in/austin" },
        { label: "Louvered Patio Covers in Austin, TX" },
      ]}
      eyebrow="★ LOUVERED ROOFS · AUSTIN, TX ★"
      title="Motorized Louvered Patio Covers in Austin, TX"
      intro="Adjustable aluminum louvered roofs — Engineered aluminum louvered pergola systems on welded aluminum frames. Open the louvers for breeze, tilt them to track the sun, close them tight when a Hill Country thunderstorm rolls in. App, remote and wall-switch control standard."
      heroImage={heroPicture}
      heroCta={{
        eyebrow: "Louvered Roofs · Serving Austin, TX",
        headline: "Louvered Roof Quote — Free On-Site Estimate",
        subhead: "Send photos and dimensions. We'll quote louver system, motor count, sensors and finish in one written estimate.",
        primaryHref: `/#lets-talk?type=${encodeURIComponent("Patio / Pergola Cover")}&notes=${encodeURIComponent("Louvered patio roof — Austin, TX.")}`,
        primaryLabel: "Get My Louvered Roof Quote",
        phoneNumber: "+1 (830) 390-9588",
      }}
      features={[
        "Motorized aluminum louver blades",
        "Open · tilt · close — full sun to full rain",
        "Wall switch, remote & smartphone app control",
        "Optional rain & wind sensors with auto-close",
        "Integrated perimeter gutters & downspouts in the posts",
        "Powder-coated welded aluminum structural frame",
      ]}
      body={[
        {
          heading: "Sun in the Morning, Shade by Noon, Cover by 4",
          text: "Austin weather changes by the hour. A solid roof is shade-only; a fabric cover is shade-only; a louvered roof is the only cover that lets you choose. Open at sunrise to warm the patio, tilt 45° by noon to filter UV, close tight when an afternoon thunderstorm builds off the Edwards Plateau.",
        },
        {
          heading: "Smart Sensors for Hill Country Weather",
          text: "Add a wind sensor and a rain sensor and the roof auto-closes before a storm hits — useful when you're not home or when the patio has rated wind exposure (Lakeway ridges, Lake Travis bluffs, Spicewood hilltops). Sensors integrate with most home-automation platforms.",
          cta: { label: "Patio covers overview", href: "/patio-covers/in/austin" },
        },
        {
          heading: "Built on Our Aluminum Frame",
          text: "Every louvered system we install in Austin sits on a welded aluminum frame fabricated in our Seguin shop — the same frame engineering as our solid covers. Posts hide the gutters and downspouts so the roof line stays clean.",
          cta: { label: "Aluminum patio covers", href: "/aluminum-patio-covers/in/austin" },
        },
      ]}
      applications={[
        "Backyard Living-Room Patios",
        "Pool Decks & Spa Areas",
        "Outdoor Kitchens",
        "Restaurant & Bar Patios",
        "Hotel & Resort Decks",
        "Rooftop Decks Downtown",
      ]}
      faqs={PAGE_FAQS}
      hideFaqJsonLd
      cityCallouts={[
        { to: "/louvered-pergolas", city: "Austin", anchorText: "Motorized louvered pergolas — full outdoor-living systems", blurb: "Configurations, upgrades, engineering and financing for motorized louvered pergolas across Texas." },
        { to: "/austin", city: "Austin", anchorText: "All Texas Made Shade services in Austin", blurb: "Every shade and cover system we offer to Austin." },
        { to: "/insulated-patio-covers/in/austin", city: "Austin", anchorText: "Insulated patio covers in Austin", blurb: "Solid foam-core alternative when full-time heat blocking matters more than flexibility." },
      ]}
      postCta={<AustinSiblingCallouts current={PATH} />}
    />
  );
}
